import * as _ from 'lodash';
import { Component } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { RouteComponentProps } from 'react-router';
import type { Column, SortingRule } from 'react-table';

import { Action, ResourceType } from '../../../../../typings/permissions';
import { WithDialogContext } from '../../../../contexts/DialogContext';
import { window } from '../../../../globals';
import RolesModule from '../../../../redux/roles';
import { icons } from '../../../generic';
import Loader from '../../../generic/Loader';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import Header from '../../../shared/settings/SettingsPageHeader';
import SettingsPageTableActions from '../../../shared/settings/SettingsPageTableActions';
import { InvalidPermissionError } from '../../Errors';
import SettingsTableRowActions from '../../shared/SettingsTableRowActions';
import type { Role } from '../../../../../typings/api';
import type { DialogState } from '../../../../contexts/DialogContext';
import type { RolesStore } from '../../../../redux/roles';
import type { IAsyncStatus } from '../../../../types';

type ReduxStateProps = {
  roles: RolesStore['entities'];
  status: IAsyncStatus;
};

type DispatchProps = {
  fetchRoles: () => void;
  deleteRole: (roleId: number) => void;
  cloneRole: (role: Partial<Role>) => void;
};

type OrganizationRolesIndexProps = RouteComponentProps<object, object> & ReduxStateProps & DispatchProps & {
  dialogContext: DialogState;
};

type OrganizationRolesIndexState = {
  sortMode: SortingRule;
  searchValue: string;
};

class OrganizationRolesIndex extends Component<OrganizationRolesIndexProps, OrganizationRolesIndexState> {
  readonly state: OrganizationRolesIndexState = {
    sortMode: {
      id: 'name',
      desc: false,
    },
    searchValue: '',
  };

  componentDidMount() {
    this.props.fetchRoles();
  }

  get userCanManageRoles() {
    return window.hasPermission({
      resourceType: ResourceType.Role,
      action: Action.Manage,
    });
  }

  onSortedChanged = (sortMode: SortingRule) => this.setState({ sortMode });

  onCloneRoleClick =
    ({ name, description, scope, permissions }: Role) =>
      () => {
        const { cloneRole } = this.props;
        cloneRole({
          id: 'add',
          name: `${name} (copy)`,
          description,
          scope,
          permissions,
          isCustom: true,
        });
        this.navigateToEditPage('clone');
      };

  getRemoveRoleTooltip = (isCustom: boolean) => {
    if (!isCustom) {
      return 'You are only able to remove custom roles';
    }
    return 'Delete Role';
  };

  getColumns = (): Array<Column<Role>> => {
    const { sortMode } = this.state;

    return [
      {
        id: 'name',
        Header: <TableHeader title="Role" sort={{ id: 'name', currentMode: sortMode }} />,
        accessor: 'name',
        width: 250,
        resizable: false,
        style: {
          textOverflow: 'ellipsis',
        },
      },
      {
        id: 'description',
        Header: <TableHeader title="Description" sort={{ id: 'description', currentMode: sortMode }} />,
        accessor: 'description',
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ value }) => (
          <span className="role-settings__table__cell__text" title={value}>
            {value}
          </span>
        ),
        resizable: false,
        style: { textOverflow: 'ellipsis', paddingRight: '15px' },
      },
      {
        id: 'actions',
        Header: <TableHeader title="Actions" />,
        accessor: () => null,
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ original: role }) => (
          <SettingsTableRowActions>
            <SettingsTableRowActions.ViewOrEditLink
              to={`/account/settings/organization/roles/${role.id}`}
              tooltip={role.isCustom ? 'Edit Role' : 'View Role'}
              tooltipId={`edit-role-${role.id}`}
              canEdit={role.isCustom}
            />
            <SettingsTableRowActions.IconButton
              icon={icons.cloneO}
              onClick={this.onCloneRoleClick(role)}
              tooltipId={`clone-role-${role.id}`}
              tooltip="Duplicate Role"
            />
            <SettingsTableRowActions.IconButton
              icon={icons.trashRegular}
              onClick={this.onRemoveRoleClick(role)}
              tooltipId={`delete-role-${role.id}`}
              tooltip={this.getRemoveRoleTooltip(role.isCustom)}
              isDisabled={!role.isCustom}
            />
          </SettingsTableRowActions>
        ),
        width: 100,
        resizable: false,
        sortable: false,
      },
    ];
  };

  get filteredRoles() {
    const roles = Object.values(this.props.roles);
    const { searchValue } = this.state;

    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      return roles.filter(
        (role: Role) =>
          role.name.toLowerCase().includes(searchLower) || role.description.toLowerCase().includes(searchLower),
      );
    }

    return roles;
  }

  get shouldShowLoader() {
    const { status, roles } = this.props;
    return status.loading || _.isEmpty(roles);
  }

  navigateToEditPage = (roleId: number | string): void => {
    const { router } = this.props;
    router.push(`/account/settings/organization/roles/${roleId}`);
  };

  onRemoveRoleClick = (role: Role) => () =>
    this.props.dialogContext.onConfirm({
      title: 'Remove Role?',
      body: `Are you sure you want to remove the "${role.name}" role?`,
      onConfirm: () => this.props.deleteRole(role.id),
      confirmText: 'Remove',
      confirmBtnType: 'error',
    });

  render() {
    if (this.shouldShowLoader) {
      return (
        <div className="role-settings__loader">
          <Loader />
        </div>
      );
    }
    const { searchValue } = this.state;

    const { filteredRoles } = this;

    if (!this.userCanManageRoles) {
      return <InvalidPermissionError />;
    }

    return (
      <>
        <Header heading="Roles" />
        <SettingsPageTableActions>
          <SettingsPageTableActions.NavigationButton
            size="large"
            type="secondary"
            to="/account/settings/organization/roles/add"
            openInNewTab={false}
          >
            Add Role
          </SettingsPageTableActions.NavigationButton>
          <SettingsPageTableActions.SearchInput
            label="Search roles"
            value={searchValue}
            onChange={(value) => this.setState({ searchValue: value })}
            placeholder="Search roles"
            size="medium"
          />
        </SettingsPageTableActions>
        <SimpleReactTable
          data={filteredRoles}
          columns={this.getColumns()}
          defaultSortMode={[
            {
              id: 'name',
              desc: false,
            },
          ]}
          onSortedChanged={this.onSortedChanged}
          showPagination={filteredRoles.length > 20}
          cacheId="organizationRoles"
        />
      </>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  roles: RolesModule.selectors.getRoles,
  status: RolesModule.selectors.getStatus,
});

const mapDispatchToProps: DispatchProps = {
  fetchRoles: RolesModule.actions.fetchAll,
  deleteRole: RolesModule.actions.delete,
  cloneRole: RolesModule.actions.cloneRole,
};

export default connect(mapStateToProps, mapDispatchToProps)(
  WithDialogContext(OrganizationRolesIndex),
);
